import { Alert, AlertTitle, Avatar, Box, Typography } from '@mui/material';
import { db } from 'app/firebase';
import { useAppSelector } from 'app/hooks';
import Loader from 'components/atoms/loader';
import StoryAvatar from 'components/molecules/Avatar/StoryAvatar';
import DMInput from 'components/molecules/InputText/DMInput';
import MessageBox, { MessageDataType } from 'components/molecules/MessageBox';
import { selectAuth } from 'features/auth/authSlice';
import {
  selectChat,
  setLoading as chatLoading,
  updateLastMessage,
} from 'features/auth/chatSlice';
import {
  collection,
  onSnapshot,
  query,
  orderBy,
  doc,
  updateDoc,
  Unsubscribe,
} from 'firebase/firestore';
import { UserData } from 'interfaces/UserData.interface';
import { FC, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import {
  DHeader,
  HeaderAvatarCont,
  LightBg,
  MessageCont,
  Section,
  StoryAvatarCont,
} from './DMessage.styles';
import {
  fetchInboxUsers,
  getRef,
  handleSendMessage,
} from 'utils/Firebase.util';
import { IMessage } from 'interfaces/message.interface';
import ChatOptions from './ChatOptions';
import { useDispatch } from 'react-redux';
import { IAuthState } from 'interfaces/auth.interfaces';
import { IChatState } from 'interfaces/chat.interfaces copy';
import { Dispatch } from 'redux';
// import { IStoryData } from 'interfaces/Story.interface';
import storiesData from 'components/organisms/StoryBody/dummyStories';
import { NormalButton } from 'components/atoms/Button';
import StoryDevModal from './StoryDevModal';

interface IInboxMessage {
  data: UserData;
  query?: string;
  currentIndex: number;
  handleStoryUI?: (i: number) => void;
  showAddStoryShown: () => void;
  hideAddStoryShown: () => void;
}

const InboxMessages: FC<IInboxMessage> = ({
  data,
  //query,
  currentIndex,
  handleStoryUI,
  showAddStoryShown,
  hideAddStoryShown,
}) => {
  const authState: IAuthState = useAppSelector(selectAuth);
  const user0 = require('assets/Profile/buku_default_avatar.png');
  // const user1 = require('assets/user1.png');
  // const user2 = require('assets/user2.png');
  const exclaiminationIcon = require('assets/icons/exclaimination-icon.svg');
  const dispatch: Dispatch = useDispatch();
  const chatState: IChatState = useAppSelector(selectChat);
  const [messages, setMessages] = useState<IMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [onlineStatus, setOnlineStatus] = useState<string>('Offline');
  const [isCreator, setIsCreator] = useState<boolean>(false);
  const scrollIntoViewElem = useRef<HTMLDivElement>(null);
  const messageCont = useRef<HTMLDivElement>(null);

  const getMessageSenderType = (item: number) => {
    if (item === authState.id) {
      return 'me';
    } else {
      return 'you';
    }
  };

  // const checkWatchedStory = (storyData: IStoryData): boolean => {
  //   let watched = true;
  //   storyData.stories.forEach(story => {
  //     if (!story.watched) {
  //       watched = false;
  //     }
  //   });
  //   return watched;
  // };

  const getMessageDataType = (item: string): MessageDataType | undefined => {
    switch (item) {
      case 'image':
        return MessageDataType.image;
      case 'video':
        return MessageDataType.video;
      case 'time':
        return MessageDataType.time;
      case 'tip':
        return MessageDataType.tip;
      case 'nft':
        return MessageDataType.nft;
      case 'invite':
        return MessageDataType.invite;
      case 'collab':
        return MessageDataType.collab;
      default:
        return undefined;
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        await fetchInboxUsers(authState, dispatch, chatLoading);
      } catch (error) {
        console.log(error);
      }
    };
    fetchUsers();
    //eslint-disable-next-line
  }, [authState.id]);

  useEffect(() => {
    if (scrollIntoViewElem.current) {
      scrollIntoViewElem.current.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
      });
    }
    window.scrollTo(0, 0);
  }, [scrollIntoViewElem, messages.length]);

  useEffect(() => {
    const checkIfUserOnline = () => {
      if (chatState.onlineUsers.includes(data.id)) {
        setOnlineStatus('Online');
      } else if (chatState.offlineUsers.includes(data.id)) {
        setOnlineStatus(`Offline ${moment().fromNow()} `);
      } else {
        setOnlineStatus(
          `Offline ${data.lastOnline ? moment(data.lastOnline).fromNow() : ''}`
        );
      }
    };
    checkIfUserOnline();
  }, [data, chatState.onlineUsers, chatState.offlineUsers]);

  useEffect(() => {
    let unsub: Unsubscribe;
    const getDocs = async () => {
      if (authState.id && data.id) {
        setLoading(true);
        setMessages([]);
        const docRef = await getRef(authState.id, data.id);
        if (!docRef) {
          return;
        }
        const q = query(
          collection(db, 'chats', docRef, 'messages'),
          orderBy('date')
        );
        unsub = onSnapshot(q, async doc => {
          for await (const message of doc.docChanges()) {
            if (message.doc.data().invisible) {
              if (message.doc.data().invisible.includes(authState.username))
                return;
            } else {
              handleNewMessage(message.doc.data(), message.type);
            }
          }
          const lastMessage = doc.docChanges().at(-1);
          if (lastMessage) {
            if (lastMessage.doc) {
              dispatch(updateLastMessage(lastMessage.doc.data() as IMessage));
            }
          }
          setLoading(false);
        });
      }
    };
    getDocs();
    return () => {
      if (unsub) {
        unsub();
      }
    };
    //eslint-disable-next-line
  }, [chatState.currentIndex, data, authState.id, authState.username]);

  const handleNewMessage = async (newMessage: any, messageType: string) => {
    if (messageType === 'removed') {
      setMessages(messages =>
        messages.filter(message => message.id !== newMessage.id)
      );
    } else if (messageType === 'modified') {
      setMessages(prevMessages => {
        const changedMsgIndex = prevMessages.findIndex(
          msg => msg.id === newMessage.id
        );
        prevMessages.splice(changedMsgIndex, 1, newMessage);
        return prevMessages;
      });
    } else {
      setMessages(messages => [...messages, newMessage]);
    }
    if (newMessage.uid !== authState.id && authState.id) {
      const docRef = await getRef(authState.id, newMessage.uid);
      if (docRef)
        await updateDoc(doc(db, 'chats', docRef, 'messages', newMessage.id), {
          isRead: true,
        });
    }
  };

  const inviteCreator = async () => {
    await handleSendMessage(
      'Join as a creator now!',
      'invite',
      authState,
      data,
      chatState,
      currentIndex
    );
  };

  useEffect(() => {
    if (scrollIntoViewElem.current) scrollIntoViewElem.current.scrollIntoView();
    window.scrollTo(0, 0);
  }, [messages.length]);

  return (
    <Section ref={messageCont} item xs={8.4} className="scrollbar-hidden">
      <StoryAvatarCont item className="scrollbar-hidden">
        <StoryDevModal>
          <StoryAvatar
            // onClick={() => showAddStoryShown()}
            icon={''}
            title={'Your Story'}
            state={'my-story'}
          />
        </StoryDevModal>
        {storiesData.map((user, i) => (
          <StoryDevModal key={i}>
            <StoryAvatar
              // onClick={() => handleStoryUI && handleStoryUI(i)}
              key={user.id}
              icon={''}
              title={''}
              // state={`${checkWatchedStory(user) ? 'watched' : 'unwatched'}`}
              state={'watched'}
            />
          </StoryDevModal>
        ))}
      </StoryAvatarCont>

      <LightBg>
        <MessageCont>
          <DHeader>
            <HeaderAvatarCont>
              <Avatar src={data?.avatar ? data.avatar : user0} />
              <Box className="text__cont">
                <Typography style={{ color: '#fff', fontWeight: 'bold' }}>
                  {data?.username}
                </Typography>
                <Typography style={{ color: '#565463', fontSize: '.8rem' }}>
                  {onlineStatus}
                </Typography>
              </Box>
            </HeaderAvatarCont>
            {isCreator && (
              <Alert
                sx={{
                  position: 'absolute',
                  width: '90%',

                  top: '120%',
                  zIndex: 150,
                }}
                severity="success"
                onClose={() => setIsCreator(false)}
              >
                <AlertTitle>Success</AlertTitle>
                You have accepted {data.username}'s invite, —{' '}
                <strong>You're a creator now!</strong>
              </Alert>
            )}
            <Box
              sx={{
                display: 'flex',
                width: 'fit-content',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              {authState.invites > 0 &&
                authState.isCreator &&
                !data.isCreator && (
                  <NormalButton onClick={inviteCreator} text="Invite Creator" />
                )}
              {authState && data && (
                <ChatOptions
                  user={data}
                  senderId={data.id}
                  authState={authState}
                >
                  <img
                    src={exclaiminationIcon.default}
                    alt="exclaiminationIcon"
                    width="20px"
                    height="20px"
                  />
                </ChatOptions>
              )}
            </Box>
          </DHeader>
          <Box
            className="scrollbar-hidden"
            style={{
              height: '50vh',
              minHeight: '50vh',
              overflowX: 'hidden',
              overflowY: 'auto',
              padding: '0rem 20px',
            }}
          >
            {messages.length > 0
              ? messages.map((item, i) => {
                  let repliedToType;
                  const messageDataType = getMessageDataType(item.type);

                  if (item.repliedTo) {
                    repliedToType = getMessageDataType(item.repliedTo.type);
                  }
                  return (
                    <Box key={i}>
                      <MessageBox
                        senderId={item.uid}
                        isRead={item.isRead}
                        isSent={item.isSent}
                        repliedTo={item.repliedTo}
                        repliedToType={repliedToType}
                        msgId={item.id}
                        username={data.username}
                        messages={messages}
                        post={item.post}
                        recieverAvatar={
                          authState.avatar ? authState.avatar : user0
                        }
                        userId={data.id}
                        senderAvatar={data?.avatar ? data.avatar : user0}
                        messageSender={getMessageSenderType(item.uid)}
                        messageDataType={messageDataType}
                        nft={item.nft}
                        text={item.text}
                        reaction={item.reaction}
                        isClicked={item.isClicked}
                        isLocked={item.isLocked}
                        isPending={item.isPending}
                        user={data}
                        scrollIntoViewElem={scrollIntoViewElem}
                        setIsCreator={setIsCreator}
                      />
                    </Box>
                  );
                })
              : loading && <Loader />}
            <div ref={scrollIntoViewElem}></div>
          </Box>
          <Box style={{ padding: '20px' }}>
            <DMInput messages={messages} mobile={false} user={data} />
          </Box>
        </MessageCont>
      </LightBg>
    </Section>
  );
};

export default InboxMessages;
